import { ComposedElement } from '../element/composed-element.builder';
import {
  ComposedSurveyPlugin,
  Page,
  PageType,
  SessionStatusBot,
  SubmissionInfo,
  SubmissionTypes,
  SurveyVariables,
} from '@hyper-inferno/survey-elements-core';

export interface BaseComposedPage {
  submissionInfo: SubmissionInfo;
  id: number;
  name: string;
  type: PageType;
  block_id: number;
  rank: number;
  progress: number;
  elements: ComposedElement[];

  pageMeta: SurveyVariables;

  navigation: {
    showForwardButton: boolean;
    showBackwardButton: boolean;
    // isSkippable: boolean;
    // captureLocation: boolean;
  };

  pageLayout: {
    globalHeader: string;
    questionHeader: string;
    questionFooter: string;
    globalFooter: string;
  };

  randomize_elements: boolean;
  pagePlugins: ComposedSurveyPlugin[];
}

export type ComposedPage = BaseComposedPage;

export class ComposedPageBuilder {
  setProgress(progress: number) {
    this.composedPage.progress = progress;
    return this;
  }
  private composedPage: ComposedPage;
  private _page: Page;

  constructor(page: Page) {
    this._page = page;
    this.composedPage = {
      name: page.name,
      id: page.id,
      //   submissionInfo: {
      //     submissionType: SubmissionTypes.CLIENT_RESPONSE,
      //     submissionPayload: {},
      //   },
      type: page.type, // or whatever mapping is appropriate
      block_id: page.block_id, // Initialized as empty or from page
      rank: page.rank, // Or a default starting rank
      progress: 0,
      navigation: {
        showForwardButton: false,
        showBackwardButton: false,
        // isSkippable: question.isSkippable,
        // captureLocation: question.captureLocation ?? false,
      },
      pageLayout: {
        globalHeader: '',
        questionHeader: '',
        questionFooter: '',
        globalFooter: '',
      },
      elements: [],
      pageMeta: {},
      randomize_elements: page.randomize_elements,
      pagePlugins: [],
      submissionInfo: {
        submissionType: SubmissionTypes.CLIENT_RESPONSE,
        submissionPayload: {
          sys_disposition_code: '',
        },
        statusChange: SessionStatusBot.IN_PROGRESS,
      },
    };
  }

  setQuestionVariables(variables: SurveyVariables): this {
    this.composedPage.pageMeta = variables;
    return this;
  }

  getVariables() {
    return this.composedPage.pageMeta;
  }

  getElements() {
    return (this.composedPage as any)[this.composedPage.type];
  }

  // setPageNavigation(
  //   jsn_activity_order: { questionId: string; firstVisit: string }[],
  // ) {
  //   // Extract relevant data

  //   // Parse the activity order into an array of question IDs
  //   const activityOrder = jsn_activity_order
  //     ? jsn_activity_order.map((q: any) => q.questionId)
  //     : [];

  //   // Determine the index of the current question
  //   const currentIndex = activityOrder.indexOf(this._page.id);
  //   const lastIndex = activityOrder.length - 1;

  //   // Logic to determine button visibility
  //   let showBackwardButton = currentIndex > 0; // Backward button if not the first element
  //   let showForwardButton = currentIndex < lastIndex; // Forward button if not the last element

  //   // Handle edge cases for empty or single-element arrays
  //   if (activityOrder.length <= 1) {
  //     showForwardButton = false;
  //     showBackwardButton = false;
  //   }
  //   this.composedPage.navigation = {
  //     showForwardButton,
  //     showBackwardButton,
  //   };

  //   return this;
  // }

  setSubmissionInfo(payload: SubmissionInfo): this {
    if (payload) {
      this.composedPage.submissionInfo = payload;
    }
    return this;
  }

  _setPageNavigation(showForwardButton: boolean, showBackwardButton: boolean) {
    this.composedPage.navigation = {
      showForwardButton,
      showBackwardButton,
    };
    return this;
  }
  
  setPageNavigation(currentSessionState: { history: (string | number)[]; pointer: number }) {
    const { showForwardButton, showBackwardButton } = getNavigationButtons(currentSessionState, this._page.id)
    this.composedPage.navigation = {
      showForwardButton,
      showBackwardButton,
    };
    return this;
  }

  setElements(elements: ComposedElement[]) {
    this.composedPage.elements = elements;
    return this;
  }

  setTheme(themeConfig: {
    globalHeader: string;
    questionHeader: string;
    questionFooter: string;
    globalFooter: string;
  }) {
    this.composedPage.pageLayout = {
      globalHeader: themeConfig.globalHeader,
      questionHeader: themeConfig.questionHeader,
      questionFooter: themeConfig.questionFooter,
      globalFooter: themeConfig.globalFooter,
    };
    return this;
  }

  public setPagePlugins(plugins: ComposedSurveyPlugin[]): this {
    this.composedPage.pagePlugins = plugins || [];
    return this;
  }

  build(): ComposedPage {
    return this.composedPage;
  }
}

function getNavigationButtons(
  navigationState: { history: (string | number)[]; pointer: number } | null,
  currentPageId: number | string
) {
  // If there is no navigation state or history is empty, don't show any buttons
  if (!navigationState || !navigationState.history || navigationState.history.length === 0) {
    return {
      showForwardButton: false,
      showBackwardButton: false
    };
  }

  const history = navigationState.history;

  // Find where the user currently is based on the active page ID
  let currentIndex = history.indexOf(currentPageId);

  // Fallback: If the page isn't in history yet (e.g., during initialization), 
  // use the state's internal pointer.
  if (currentIndex === -1) {
    currentIndex = navigationState.pointer;
  }

  const lastIndex = history.length - 1;

  // - Show backward if the current index is past the first page (index 0)
  // - Show forward if the current index is behind the last page in history
  const showBackwardButton = currentIndex > 0;
  const showForwardButton = currentIndex < lastIndex;

  return {
    showForwardButton,
    showBackwardButton
  };
}